// TennisTrack Hub — Form Crea/Modifica Template | 029 Project
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Template.css';

const COLPI_TENNIS = [
  'Dritto', 'Rovescio', 'Servizio', 'Volée', 'Smash',
  'Slice', 'Pallonetto', 'Drop shot', 'Topspin', 'Approccio', 'Difensivo', 'Altro'
];

const TIPOLOGIE_FITNESS = [
  'Forza', 'Resistenza', 'Flessibilità', 'Velocità', 'Coordinazione',
  'Equilibrio', 'Core', 'Cardio', 'Recupero', 'Altro'
];

const esercizioTennisBianco = () => ({
  colpo: '', difficolta: 5, descrizione: '', serie: '', ripetizioni: '', durata_min: ''
});

const esercizioFitnessBianco = () => ({
  nome: '', tipologia: '', serie: '', ripetizioni: '', carico_kg: '', note: ''
});

const FormTemplate = () => {
  const { id } = useParams(); // presente solo in modifica
  const navigate = useNavigate();
  const isModifica = Boolean(id);

  const [tipo, setTipo] = useState('tennis');
  const [titolo, setTitolo] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [livello, setLivello] = useState('principiante');
  const [esercizi, setEsercizi] = useState([esercizioTennisBianco()]);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');
  const [suggerimento, setSuggerimento] = useState('');
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Carica dati se modifica
  useEffect(() => {
    if (!isModifica) return;
    setLoadingEdit(true);
    api.get(`/template/miei/${id}`)
      .then(res => {
        const t = res.data.template;
        setTipo(t.tipo || 'tennis');
        setTitolo(t.titolo || '');
        setDescrizione(t.descrizione || '');
        setLivello(t.livello || 'principiante');
        const contenuto = typeof t.contenuto === 'string'
          ? JSON.parse(t.contenuto)
          : t.contenuto;
        const es = contenuto?.esercizi;
        if (Array.isArray(es) && es.length > 0) {
          setEsercizi(es);
        } else {
          setEsercizi(t.tipo === 'fitness'
            ? [esercizioFitnessBianco()]
            : [esercizioTennisBianco()]
          );
        }
      })
      .catch(() => setErrore('Errore nel caricamento del template.'))
      .finally(() => setLoadingEdit(false));
  }, [id, isModifica]);

  const cambioTipo = (nuovoTipo) => {
    if (nuovoTipo === tipo) return;
    setTipo(nuovoTipo);
    setEsercizi(nuovoTipo === 'fitness'
      ? [esercizioFitnessBianco()]
      : [esercizioTennisBianco()]
    );
  };

  const aggiornaEsercizio = (idx, campo, valore) => {
    setEsercizi(prev => prev.map((e, i) => i === idx ? { ...e, [campo]: valore } : e));
  };

  const aggiungiEsercizio = () => {
    setEsercizi(prev => [
      ...prev,
      tipo === 'fitness' ? esercizioFitnessBianco() : esercizioTennisBianco()
    ]);
  };

  const rimuoviEsercizio = (idx) => {
    if (esercizi.length <= 1) return;
    setEsercizi(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrore('');
    setSuggerimento('');
    if (!titolo.trim()) { setErrore('Il titolo è obbligatorio.'); return; }

    setLoading(true);
    const payload = {
      titolo: titolo.trim(),
      descrizione: descrizione.trim(),
      tipo,
      livello,
      contenuto: { esercizi }
    };

    try {
      if (isModifica) {
        await api.put(`/template/miei/${id}`, payload);
        navigate(`/template/miei/${id}`);
      } else {
        const res = await api.post('/template/miei', payload);
        if (res.data.suggerimento) setSuggerimento(res.data.suggerimento);
        navigate('/template');
      }
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nel salvataggio.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingEdit) {
    return (
      <div className="page-layout">
        <Navbar />
        <main className="page-main"><div className="loading-msg">Caricamento...</div></main>
      </div>
    );
  }

  return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">

        {/* Header */}
        <div className="page-header">
          <div>
            <h1>{isModifica ? '✏️ Modifica Template' : '📋 Nuovo Template'}</h1>
            <p>{isModifica ? 'Aggiorna i dettagli del tuo template' : 'Crea un template riutilizzabile'}</p>
          </div>
          <button className="btn btn-secondary" onClick={() => navigate('/template')}>
            ← Indietro
          </button>
        </div>

        {errore && <div className="alert alert-error">{errore}</div>}
        {suggerimento && <div className="alert alert-info">💡 {suggerimento}</div>}

        <form className="card form-template" onSubmit={handleSubmit}>

          {/* Tipo */}
          <div className="form-group">
            <label className="form-label">Tipo di allenamento</label>
            <div className="tipo-selector">
              <button type="button" className={`tipo-btn ${tipo === 'tennis' ? 'active' : ''}`} onClick={() => cambioTipo('tennis')}>
                🎾 Tennis
              </button>
              <button type="button" className={`tipo-btn ${tipo === 'fitness' ? 'active' : ''}`} onClick={() => cambioTipo('fitness')}>
                💪 Fitness
              </button>
            </div>
          </div>

          {/* Titolo */}
          <div className="form-group">
            <label className="form-label">Titolo *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Es. Allenamento base rovescio"
              value={titolo}
              onChange={e => setTitolo(e.target.value)}
              maxLength={120}
              required
            />
          </div>

          {/* Descrizione */}
          <div className="form-group">
            <label className="form-label">Descrizione (opzionale)</label>
            <textarea
              className="form-textarea"
              placeholder="Descrivi brevemente l'obiettivo del template..."
              value={descrizione}
              onChange={e => setDescrizione(e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Livello */}
          <div className="form-group">
            <label className="form-label">Livello</label>
            <select className="form-select" value={livello} onChange={e => setLivello(e.target.value)}>
              <option value="principiante">Principiante</option>
              <option value="intermedio">Intermedio</option>
              <option value="avanzato">Avanzato</option>
            </select>
          </div>

          {/* ── ESERCIZI BUILDER ── */}
          <div className="esercizi-section">
            <h3>
              {tipo === 'tennis' ? '🎾' : '💪'} Esercizi del template
              <span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-secondary)' }}>
                &nbsp;({esercizi.length})
              </span>
            </h3>

            <div className="esercizi-list">
              {esercizi.map((es, idx) => (
                <div key={idx} className="esercizio-item">
                  <div className="esercizio-header">
                    <span className="esercizio-num">#{idx + 1}</span>
                    {esercizi.length > 1 && (
                      <button
                        type="button"
                        className="btn-text-danger"
                        onClick={() => rimuoviEsercizio(idx)}
                      >
                        🗑 Rimuovi
                      </button>
                    )}
                  </div>

                  {tipo === 'tennis' ? (
                    <>
                      <div className="esercizio-grid">
                        <div className="form-group">
                          <label className="form-label">Colpo</label>
                          <select
                            className="form-select"
                            value={es.colpo}
                            onChange={e => aggiornaEsercizio(idx, 'colpo', e.target.value)}
                          >
                            <option value="">-- Seleziona --</option>
                            {COLPI_TENNIS.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Difficoltà: {es.difficolta}/10</label>
                          <div className="difficolta-row">
                            <input
                              type="range"
                              min="1"
                              max="10"
                              value={es.difficolta}
                              onChange={e => aggiornaEsercizio(idx, 'difficolta', Number(e.target.value))}
                            />
                            <span className="difficolta-value">{es.difficolta}</span>
                          </div>
                        </div>
                      </div>
                      <div className="esercizio-grid triple">
                        <div className="form-group">
                          <label className="form-label">Serie</label>
                          <input
                            type="number"
                            className="form-input"
                            placeholder="3"
                            min="1"
                            value={es.serie}
                            onChange={e => aggiornaEsercizio(idx, 'serie', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Ripetizioni</label>
                          <input
                            type="number"
                            className="form-input"
                            placeholder="10"
                            min="1"
                            value={es.ripetizioni}
                            onChange={e => aggiornaEsercizio(idx, 'ripetizioni', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Durata (min)</label>
                          <input
                            type="number"
                            className="form-input"
                            placeholder="15"
                            min="1"
                            value={es.durata_min}
                            onChange={e => aggiornaEsercizio(idx, 'durata_min', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Descrizione esercizio</label>
                        <textarea
                          className="form-textarea"
                          placeholder="Dettagli sull'esecuzione..."
                          rows={2}
                          value={es.descrizione}
                          onChange={e => aggiornaEsercizio(idx, 'descrizione', e.target.value)}
                          maxLength={400}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="esercizio-grid">
                        <div className="form-group">
                          <label className="form-label">Nome esercizio</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Es. Squat"
                            value={es.nome}
                            onChange={e => aggiornaEsercizio(idx, 'nome', e.target.value)}
                            maxLength={80}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Tipologia</label>
                          <select
                            className="form-select"
                            value={es.tipologia}
                            onChange={e => aggiornaEsercizio(idx, 'tipologia', e.target.value)}
                          >
                            <option value="">-- Seleziona --</option>
                            {TIPOLOGIE_FITNESS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="esercizio-grid triple">
                        <div className="form-group">
                          <label className="form-label">Serie</label>
                          <input
                            type="number"
                            className="form-input"
                            placeholder="3"
                            min="1"
                            value={es.serie}
                            onChange={e => aggiornaEsercizio(idx, 'serie', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Ripetizioni</label>
                          <input
                            type="number"
                            className="form-input"
                            placeholder="12"
                            min="1"
                            value={es.ripetizioni}
                            onChange={e => aggiornaEsercizio(idx, 'ripetizioni', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Carico (kg)</label>
                          <input
                            type="number"
                            className="form-input"
                            placeholder="50"
                            min="0"
                            step="0.5"
                            value={es.carico_kg}
                            onChange={e => aggiornaEsercizio(idx, 'carico_kg', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Note</label>
                        <textarea
                          className="form-textarea"
                          placeholder="Note aggiuntive..."
                          rows={2}
                          value={es.note}
                          onChange={e => aggiornaEsercizio(idx, 'note', e.target.value)}
                          maxLength={300}
                        />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={aggiungiEsercizio}
              style={{ marginTop: '12px' }}
            >
              + Aggiungi esercizio
            </button>
          </div>

          {/* Submit */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/template')}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Salvataggio...' : isModifica ? '💾 Aggiorna template' : '💾 Crea template'}
            </button>
          </div>
        </form>

      </main>
    </div>
  );
};

export default FormTemplate;
